import React, { useState, useEffect, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Image from '../../../components/AppImage';
import jobApplicationService from '../../../services/jobApplicationService';
import NotificationToast from '../../../components/ui/NotificationToast';
import { STAGES } from './PipelineTracker';

const DuplicateApplicationsView = ({ onApplicantClick, onDataChange }) => {
  const [loading, setLoading] = useState(true);
  const [rescanning, setRescanning] = useState(false);
  const [error, setError] = useState(null);
  const [duplicateData, setDuplicateData] = useState({
    summary: {
      total_groups: 0,
      total_redundant_filings: 0,
      total_applications_in_groups: 0
    },
    groups: []
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // all | email | phone | both
  const [notification, setNotification] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteConfirmApp, setDeleteConfirmApp] = useState(null);

  // Load duplicate groups
  const loadDuplicates = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await jobApplicationService.getDuplicates();
      if (result.success && result.data) {
        setDuplicateData(result.data);
      } else {
        throw new Error(result.error || 'Failed to load duplicate applications');
      }
    } catch (err) {
      console.error('Error loading duplicates:', err);
      setError(err.message || 'Failed to load duplicate applications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDuplicates();
  }, []);

  // Recalculate / Rescan all applications
  const handleRescan = async () => {
    try {
      setRescanning(true);
      const result = await jobApplicationService.recalculateDuplicates();
      if (result.success) {
        setNotification({
          type: 'success',
          message: result.message || 'Database rescan complete! Duplicate clusters updated.'
        });
        await loadDuplicates();
        onDataChange?.();
      } else {
        throw new Error(result.error || 'Failed to rescan duplicates');
      }
    } catch (err) {
      setNotification({
        type: 'error',
        message: err.message || 'Failed to rescan database'
      });
    } finally {
      setRescanning(false);
    }
  };

  // Delete duplicate application
  const handleDelete = async (app) => {
    try {
      setDeletingId(app.id);
      const result = await jobApplicationService.deleteApplication(app.id);
      if (result.success) {
        setNotification({
          type: 'success',
          message: `Application for ${app.position || 'job'} deleted successfully.`
        });
        setDeleteConfirmApp(null);
        await loadDuplicates();
        onDataChange?.();
      } else {
        throw new Error(result.error || 'Failed to delete application');
      }
    } catch (err) {
      setNotification({
        type: 'error',
        message: err.message || 'Failed to delete application'
      });
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStageBadge = (stageKey) => {
    const norm = (stageKey === 'ai_screening' || stageKey === 'hr_screening') ? 'screening' : stageKey;
    const stage = STAGES.find((s) => s.key === norm || s.key === stageKey);
    const label = stage ? stage.label : (stageKey === 'ai_screening' || stageKey === 'hr_screening' ? 'AI & HR Screening' : (stageKey || 'Applied'));
    return (
      <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-secondary text-secondary-foreground border border-border">
        {label}
      </span>
    );
  };

  const getAtsScoreBadge = (score) => {
    if (score === null || score === undefined || score === '') return null;
    const numScore = Math.round(Number(score));
    const color =
      numScore >= 80 ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800' :
      numScore >= 60 ? 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800' :
      'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-800';
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold border ${color}`} title="ATS Match Score">
        <Icon name="Target" size={12} />
        {numScore}% Match
      </span>
    );
  };

  const getReasonBadge = (reason) => {
    const r = (reason || 'email').toLowerCase();
    if (r === 'both') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-purple-50 text-purple-700 border border-purple-200 dark:bg-purple-950/40 dark:text-purple-300 dark:border-purple-800">
          <Icon name="ShieldAlert" size={11} />
          Email + Phone Match
        </span>
      );
    }
    if (r === 'phone') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800">
          <Icon name="Phone" size={11} />
          Phone Match
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800">
        <Icon name="Mail" size={11} />
        Email Match
      </span>
    );
  };

  // Filter groups by search term and match type
  const filteredGroups = useMemo(() => {
    return duplicateData.groups.filter((group) => {
      const matchesType =
        filterType === 'all' ||
        (filterType === 'email' && group.match_type === 'email') ||
        (filterType === 'phone' && group.match_type === 'phone') ||
        (filterType === 'both' && group.match_type === 'both');

      if (!matchesType) return false;

      if (!searchTerm.trim()) return true;

      const q = searchTerm.toLowerCase();
      const name = (group.candidate_name || '').toLowerCase();
      const matchVal = (group.match_value || '').toLowerCase();
      const masterPos = (group.master_record?.position || '').toLowerCase();
      const dupPositions = (group.duplicate_records || []).map((d) => (d.position || '').toLowerCase()).join(' ');

      return name.includes(q) || matchVal.includes(q) || masterPos.includes(q) || dupPositions.includes(q);
    });
  }, [duplicateData.groups, searchTerm, filterType]);

  return (
    <div className="space-y-6">
      {notification && (
        <NotificationToast
          message={notification.message}
          type={notification.type}
          onDismiss={() => setNotification(null)}
        />
      )}

      {/* Top Banner & Summary Cards */}
      <div className="bg-card rounded-xl border border-border p-6 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 pb-6 border-b border-border">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="p-1.5 rounded-lg bg-primary/10 text-primary">
                <Icon name="Layers" size={20} />
              </span>
              <h2 className="text-xl font-bold text-foreground">Duplicate Application Detection</h2>
              <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300">
                {duplicateData.summary.total_redundant_filings} Redundant Filings
              </span>
            </div>
            <p className="text-sm text-muted-foreground">
              Automatically flags repeated applications with the same email or phone number across different or same jobs. The earliest filing is preserved as Master while redundant submissions can be purged.
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              iconName="RefreshCw"
              iconPosition="left"
              onClick={loadDuplicates}
              disabled={loading || rescanning}
            >
              Refresh
            </Button>
            <Button
              variant="default"
              size="sm"
              iconName={rescanning ? "Loader2" : "Scan"}
              iconPosition="left"
              onClick={handleRescan}
              disabled={loading || rescanning}
            >
              {rescanning ? 'Scanning...' : 'Rescan Database'}
            </Button>
          </div>
        </div>

        {/* KPI Metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-6">
          <div className="p-4 rounded-lg bg-muted/40 border border-border/60">
            <div className="flex items-center justify-between text-muted-foreground mb-1">
              <span className="text-xs font-medium uppercase tracking-wider">Duplicate Clusters</span>
              <Icon name="Users" size={16} />
            </div>
            <p className="text-2xl font-bold text-foreground">{duplicateData.summary.total_groups}</p>
            <p className="text-xs text-muted-foreground mt-1">Unique candidate identities with multiple filings</p>
          </div>

          <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <div className="flex items-center justify-between text-amber-700 dark:text-amber-400 mb-1">
              <span className="text-xs font-medium uppercase tracking-wider">Redundant Filings</span>
              <Icon name="Copy" size={16} />
            </div>
            <p className="text-2xl font-bold text-amber-700 dark:text-amber-400">
              {duplicateData.summary.total_redundant_filings}
            </p>
            <p className="text-xs text-amber-700/80 dark:text-amber-400/80 mt-1">
              Secondary applications detected after master
            </p>
          </div>

          <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
            <div className="flex items-center justify-between text-emerald-700 dark:text-emerald-400 mb-1">
              <span className="text-xs font-medium uppercase tracking-wider">Master Profiles Kept</span>
              <Icon name="ShieldCheck" size={16} />
            </div>
            <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-400">
              {duplicateData.summary.total_groups}
            </p>
            <p className="text-xs text-emerald-700/80 dark:text-emerald-400/80 mt-1">
              Protected primary records with earliest filing date
            </p>
          </div>

          <div className="p-4 rounded-lg bg-primary/10 border border-primary/20">
            <div className="flex items-center justify-between text-primary mb-1">
              <span className="text-xs font-medium uppercase tracking-wider">Total in Clusters</span>
              <Icon name="FileStack" size={16} />
            </div>
            <p className="text-2xl font-bold text-primary">
              {duplicateData.summary.total_applications_in_groups}
            </p>
            <p className="text-xs text-muted-foreground mt-1">Total application records evaluated in duplicate sets</p>
          </div>
        </div>
      </div>

      {/* Search & Filter Bar */}
      <div className="bg-card rounded-xl border border-border p-4 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="w-full sm:w-80 relative">
          <Input
            type="search"
            placeholder="Search candidate, email, phone, role..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9"
          />
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
            <Icon name="Search" size={16} />
          </div>
        </div>

        <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
          <label className="text-xs text-muted-foreground whitespace-nowrap">Filter Match Type:</label>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-2 bg-background border border-input rounded-md text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="all">All Match Types</option>
            <option value="email">Email Match Only</option>
            <option value="phone">Phone Match Only</option>
            <option value="both">Both Email & Phone</option>
          </select>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="py-16 text-center">
          <div className="animate-spin rounded-full h-9 w-9 border-b-2 border-primary mx-auto mb-3"></div>
          <p className="text-sm text-muted-foreground font-medium">Scanning and grouping duplicate applications...</p>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-xl p-5 text-red-800 dark:text-red-300">
          <div className="flex items-center gap-3">
            <Icon name="AlertTriangle" size={20} className="text-red-600" />
            <div>
              <p className="font-semibold">Error Loading Duplicates</p>
              <p className="text-sm mt-0.5">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Empty State when no duplicates found */}
      {!loading && !error && filteredGroups.length === 0 && (
        <div className="bg-card rounded-xl border border-border p-12 text-center shadow-sm">
          <div className="w-16 h-16 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mx-auto mb-4 border border-emerald-500/20">
            <Icon name="CheckCircle2" size={32} />
          </div>
          <h3 className="text-lg font-bold text-foreground mb-1">
            {searchTerm || filterType !== 'all' ? 'No Matching Duplicate Clusters' : 'All Clean — No Duplicate Applications Found'}
          </h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto mb-6">
            {searchTerm || filterType !== 'all'
              ? 'Try adjusting your search criteria or resetting filters.'
              : 'Every applicant in the system currently has a distinct email and phone number. Any future duplicate submissions will be flagged here automatically.'}
          </p>
          {(searchTerm || filterType !== 'all') ? (
            <Button
              variant="outline"
              onClick={() => {
                setSearchTerm('');
                setFilterType('all');
              }}
            >
              Clear Filters
            </Button>
          ) : (
            <Button
              variant="outline"
              iconName="Scan"
              iconPosition="left"
              onClick={handleRescan}
              disabled={rescanning}
            >
              {rescanning ? 'Scanning...' : 'Re-verify Entire Database'}
            </Button>
          )}
        </div>
      )}

      {/* Duplicate Groups List */}
      {!loading && !error && filteredGroups.length > 0 && (
        <div className="space-y-6">
          {filteredGroups.map((group, groupIdx) => {
            const master = group.master_record;
            const duplicates = group.duplicate_records || [];

            return (
              <div
                key={group.group_id || groupIdx}
                className="bg-card rounded-xl border border-border shadow-sm overflow-hidden transition-all hover:border-border/80"
              >
                {/* Group Header */}
                <div className="bg-muted/40 px-6 py-4 border-b border-border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="px-2.5 py-1 rounded-md text-xs font-bold uppercase tracking-wider bg-primary/10 text-primary border border-primary/20">
                      BY {group.match_type === 'both' ? 'EMAIL & PHONE' : group.match_type.toUpperCase()}
                    </span>
                    <span className="font-semibold text-foreground text-sm">
                      {group.match_value}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      ({group.candidate_name})
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
                      <Icon name="Layers" size={12} />
                      {group.filings_count} filings detected ({group.redundant_count} redundant)
                    </span>
                  </div>
                </div>

                {/* Group Body: Master Record + Redundant Filings Grid */}
                <div className="p-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
                  {/* Left Column: Master Profile (Keep) */}
                  <div className="lg:col-span-5 flex flex-col justify-between p-5 rounded-xl bg-emerald-500/[0.04] border border-emerald-500/30 dark:bg-emerald-950/20 dark:border-emerald-800">
                    <div>
                      {/* Master Card Header */}
                      <div className="flex items-center justify-between gap-2 mb-4 pb-3 border-b border-emerald-500/20">
                        <div className="flex items-center gap-2">
                          <span className="p-1 rounded-md bg-emerald-600 text-white">
                            <Icon name="ShieldCheck" size={16} />
                          </span>
                          <div>
                            <p className="text-xs font-bold tracking-wider uppercase text-emerald-700 dark:text-emerald-400">
                              KEEP (Master Profile)
                            </p>
                            <p className="text-[11px] text-muted-foreground">Original Application</p>
                          </div>
                        </div>
                        <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-200 border border-emerald-300 dark:border-emerald-700">
                          EARLIEST FILING
                        </span>
                      </div>

                      {/* Master Candidate Details */}
                      <div className="flex items-start gap-3 mb-4">
                        <Image
                          src={`https://ui-avatars.com/api/?name=${encodeURIComponent(master.applicant_name || 'User')}&background=059669&color=fff`}
                          alt={master.applicant_name}
                          className="w-12 h-12 rounded-full object-cover border-2 border-emerald-500/30"
                        />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-bold text-foreground text-base truncate">
                            {master.applicant_name}
                          </h4>
                          <p className="text-sm font-medium text-emerald-700 dark:text-emerald-400">
                            {master.position || master.marketplace_job_title || 'Position'}
                          </p>
                          {master.department && (
                            <p className="text-xs text-muted-foreground">Dept: {master.department}</p>
                          )}
                        </div>
                      </div>

                      {/* Master Info Grid */}
                      <div className="space-y-2 text-xs mb-4">
                        <div className="flex items-center justify-between py-1 border-b border-border/50">
                          <span className="text-muted-foreground">Applied Date:</span>
                          <span className="font-semibold text-foreground">
                            {formatDate(master.applied_date || master.created_at)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between py-1 border-b border-border/50">
                          <span className="text-muted-foreground">Email:</span>
                          <span className="font-medium text-foreground truncate max-w-[200px]" title={master.email}>
                            {master.email}
                          </span>
                        </div>
                        <div className="flex items-center justify-between py-1 border-b border-border/50">
                          <span className="text-muted-foreground">Phone:</span>
                          <span className="font-medium text-foreground">{master.phone || 'N/A'}</span>
                        </div>
                        <div className="flex items-center justify-between py-1 border-b border-border/50">
                          <span className="text-muted-foreground">Stage / Status:</span>
                          <div className="flex items-center gap-1.5">
                            {getStageBadge(master.pipeline_stage || master.status)}
                          </div>
                        </div>
                        {master.ats_score !== null && master.ats_score !== undefined && (
                          <div className="flex items-center justify-between py-1 border-b border-border/50">
                            <span className="text-muted-foreground">ATS Score:</span>
                            {getAtsScoreBadge(master.ats_score)}
                          </div>
                        )}
                        {master.resume_url && (
                          <div className="flex items-center justify-between py-1">
                            <span className="text-muted-foreground">Resume:</span>
                            <a
                              href={master.resume_url}
                              target="_blank"
                              rel="noreferrer"
                              className="text-primary hover:underline inline-flex items-center gap-1 font-medium"
                            >
                              <Icon name="FileText" size={12} />
                              View CV
                            </a>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="pt-3 border-t border-emerald-500/20">
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full border-emerald-500/40 hover:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                        iconName="Eye"
                        iconPosition="left"
                        onClick={() => onApplicantClick?.(master)}
                      >
                        View Master Details
                      </Button>
                    </div>
                  </div>

                  {/* Right Column: Redundant Filings to Purge */}
                  <div className="lg:col-span-7 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center justify-between mb-3 pb-2 border-b border-border">
                        <div className="flex items-center gap-2">
                          <span className="p-1 rounded-md bg-amber-500/20 text-amber-700 dark:text-amber-300">
                            <Icon name="Trash2" size={14} />
                          </span>
                          <h4 className="text-xs font-bold tracking-wider uppercase text-amber-700 dark:text-amber-400">
                            Redundant Filings to Review / Purge ({duplicates.length})
                          </h4>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          Delete individual duplicates safely
                        </span>
                      </div>

                      {/* Duplicate Rows List */}
                      <div className="space-y-3">
                        {duplicates.map((dup) => (
                          <div
                            key={dup.id}
                            className="p-3.5 rounded-lg border border-amber-300/40 bg-amber-500/[0.02] dark:bg-amber-950/10 dark:border-amber-800/40 hover:bg-amber-500/[0.05] transition-colors"
                          >
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-semibold text-sm text-foreground">
                                  {dup.position || dup.marketplace_job_title || 'Position'}
                                </span>
                                {getReasonBadge(dup.duplicate_reason)}
                              </div>

                              <div className="flex items-center gap-2">
                                {getStageBadge(dup.pipeline_stage || dup.status)}
                                {getAtsScoreBadge(dup.ats_score)}
                              </div>
                            </div>

                            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground pt-2 border-t border-border/40">
                              <div className="flex items-center gap-3">
                                <span>
                                  Applied: <strong className="text-foreground">{formatDate(dup.applied_date || dup.created_at)}</strong>
                                </span>
                                {dup.department && <span>Dept: {dup.department}</span>}
                                {dup.resume_url && (
                                  <a
                                    href={dup.resume_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-primary hover:underline inline-flex items-center gap-1"
                                  >
                                    <Icon name="FileText" size={11} />
                                    CV
                                  </a>
                                )}
                              </div>

                              <div className="flex items-center gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 px-2 text-xs"
                                  onClick={() => onApplicantClick?.(dup)}
                                >
                                  <Icon name="Eye" size={13} className="mr-1" />
                                  View
                                </Button>
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  className="h-7 px-2 text-xs"
                                  disabled={deletingId === dup.id}
                                  onClick={() => setDeleteConfirmApp(dup)}
                                >
                                  <Icon name="Trash2" size={13} className="mr-1" />
                                  {deletingId === dup.id ? 'Purging...' : 'Purge'}
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="mt-4 p-3 rounded-lg bg-muted/40 border border-border text-xs text-muted-foreground flex items-center gap-2">
                      <Icon name="Info" size={14} className="text-primary flex-shrink-0" />
                      <span>
                        Purging a redundant filing removes only that specific duplicate application. The Master Profile and any other filings remain intact.
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmApp && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-in fade-in duration-200">
          <div className="bg-card border border-border rounded-xl max-w-md w-full p-6 shadow-2xl space-y-4">
            <div className="flex items-center gap-3 text-red-600 dark:text-red-400">
              <div className="p-2 rounded-full bg-red-100 dark:bg-red-950/60 border border-red-200 dark:border-red-800">
                <Icon name="Trash2" size={24} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-foreground">Purge Duplicate Filing?</h3>
                <p className="text-xs text-muted-foreground">Action cannot be undone</p>
              </div>
            </div>

            <div className="p-3.5 rounded-lg bg-muted/50 border border-border text-xs space-y-1.5">
              <p><strong className="text-foreground">Candidate:</strong> {deleteConfirmApp.applicant_name}</p>
              <p><strong className="text-foreground">Position:</strong> {deleteConfirmApp.position}</p>
              <p><strong className="text-foreground">Applied Date:</strong> {formatDate(deleteConfirmApp.applied_date || deleteConfirmApp.created_at)}</p>
              <p><strong className="text-foreground">Email:</strong> {deleteConfirmApp.email}</p>
              <p><strong className="text-foreground">Phone:</strong> {deleteConfirmApp.phone || 'N/A'}</p>
            </div>

            <p className="text-xs text-muted-foreground">
              This will permanently delete this redundant application from the ATS pipeline. The master candidate profile will remain safe.
            </p>

            <div className="flex items-center justify-end gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setDeleteConfirmApp(null)}
                disabled={deletingId === deleteConfirmApp.id}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => handleDelete(deleteConfirmApp)}
                disabled={deletingId === deleteConfirmApp.id}
              >
                {deletingId === deleteConfirmApp.id ? 'Deleting...' : 'Yes, Purge Application'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DuplicateApplicationsView;
